import { useEffect, useRef, useCallback } from 'react';
import { FormFieldComponentProps } from './fieldProps';

declare global {
	interface Window {
		grecaptcha?: {
			render: (container: HTMLElement, options: Record<string, unknown>) => number;
			reset: (widgetId: number) => void;
			getResponse: (widgetId: number) => string;
		};
		onRecaptchaLoaded?: () => void;
	}
}

let recaptchaScriptLoaded = false;
const recaptchaCallbacks: (() => void)[] = [];

function loadRecaptchaScript() {
	if (recaptchaScriptLoaded) return;
	recaptchaScriptLoaded = true;

	window.onRecaptchaLoaded = () => {
		for (const cb of recaptchaCallbacks) cb();
		recaptchaCallbacks.length = 0;
	};

	const script = document.createElement('script');
	script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoaded&render=explicit';
	script.async = true;
	script.defer = true;
	document.head.appendChild(script);
}

const RecaptchaField = ({ field, onChange }: FormFieldComponentProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const widgetIdRef = useRef<number | null>(null);

	const renderCaptcha = useCallback(() => {
		if (!containerRef.current || !window.grecaptcha || widgetIdRef.current !== null) return;

		const siteKey = (field.settings.siteKey as string) ?? '';
		if (!siteKey) return;

		widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
			sitekey: siteKey,
			callback: (response: string) => onChange(response),
			'expired-callback': () => onChange(''),
		});
	}, [field.settings.siteKey, onChange]);

	useEffect(() => {
		loadRecaptchaScript();

		if (window.grecaptcha) {
			renderCaptcha();
		} else {
			recaptchaCallbacks.push(renderCaptcha);
		}
	}, [renderCaptcha]);

	return (
		<>
			{field.label && <div className='biz-form-field__label'>{field.label}</div>}
			<div ref={containerRef} className='biz-form-field__recaptcha' />
		</>
	);
};

export default RecaptchaField;
